import React, { useEffect, useState } from "react";
import { config } from "../config/app.config";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Text, Actions, IconPrint } from "braid-design-system";
import { getPlAccount } from "../services/plAccountService";
import { fetchRepaymentSchedule } from "../services/loanAppraisalForm3Service";
import apiClient from "../clients/apiClient";
import Loading from "../components/Loading";
import { formatCurrency } from "../utils/currencyUtils";
import { formatDateYMD } from "../utils/dateUtils";
import { useToast } from "../hooks/useToast";

const labels: { [key: string]: { [lang: string]: string } } = {
  title: {
    en: "LOAN REPAYMENT SCHEDULE",
    si: "ණය පියවීමේ කාලසටහන",
    ta: "கடன் திருப்பிச் செலுத்தும் அட்டவணை"
  },
  branch: {
    en: "Branch",
    si: "ශාඛාව",
    ta: "கிளை"
  },
  date: {
    en: "Date",
    si: "දිනය",
    ta: "தேதி"
  },
  loanNo: {
    en: "Loan Account Number",
    si: "ණය ගිණුම් අංකය",
    ta: "கடன் கணக்கு எண்"
  },
  productName: {
    en: "Product Name",
    si: "නිෂ්පාදන නාමය",
    ta: "தயாரிப்பு பெயர்"
  },
  capital: {
    en: "Loan Capital",
    si: "ණය මුදල",
    ta: "கடன் தொகை"
  },
  interest: {
    en: "Interest Rate",
    si: "පොලී අනුපාතය",
    ta: "வட்டி விகிதம்"
  },
  customerName: {
    en: "Customer Name",
    si: "ගනුදෙනුකරුගේ නම",
    ta: "வாடிக்கையாளர் பெயர்"
  },
  nic: {
    en: "NIC",
    si: "ජා.හැ.අංකය",
    ta: "தேசிய அடையாள அட்டை எண்"
  },
  insNumber: {
    en: "Ins No",
    si: "වාරික අංකය",
    ta: "தவணை எண்"
  },
  dueDate: {
    en: "Due Date",
    si: "ගෙවිය යුතු දිනය",
    ta: "செலுத்த வேண்டிய தேதி"
  },
  principal: {
    en: "Principal",
    si: "මූලධනය",
    ta: "அசல்"
  },
  interestCol: {
    en: "Interest",
    si: "පොලිය",
    ta: "வட்டி"
  },
  totalInstallment: {
    en: "Total Installment",
    si: "මුළු වාරිකය",
    ta: "மொத்த தவணை"
  },
  balance: {
    en: "Balance",
    si: "ණය ශේෂය",
    ta: "நிலுவை"
  },
  total: {
    en: "Total",
    si: "එකතුව",
    ta: "மொத்தம்"
  },
  period: {
    en: "Loan Period",
    si: "ණය කාලසීමාව",
    ta: "கடன் காலம்"
  }
};

const LoanSchedulePrint: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountId = Number(queryParams.get("accountId"));

  const { i18n } = useTranslation();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [printLang, setPrintLang] = useState<string>(i18n.language || localStorage.getItem('app_lang') || 'en');

  useEffect(() => {
    if (account) {
      const instLogo = account.institute_logo || account.branch?.institute?.logo;
      if (instLogo) {
        if (instLogo.startsWith('data:') || instLogo.startsWith('http')) {
          setLogoUrl(instLogo);
        } else {
          const cleanedPath = instLogo.startsWith('/') ? instLogo : `/${instLogo}`;
          let urlPath = cleanedPath;
          if (urlPath.startsWith('/uploads/')) {
            urlPath = urlPath.replace('/uploads/', '/api/uploads/');
          }
          const backendHost = config.apiUrl.replace(/\/api\/?$/, '');
          setLogoUrl(`${backendHost}/api/uploads/${instLogo.replace('uploads/', '')}`);
        }
      } else {
        setLogoUrl(undefined);
      }
    }
  }, [account]);

  const getNormalizedLang = () => {
    const l = (printLang || "en").toLowerCase();
    if (l.startsWith("si")) return "si";
    if (l.startsWith("ta")) return "ta";
    return "en";
  };
  const normLang = getNormalizedLang();

  const getLabel = (key: string) => {
    return labels[key]?.[normLang] || labels[key]?.["en"] || "";
  };

  const getCustomerById = async (customerId: number) => {
    try {
      const response = await apiClient.get(`/customer/id/${customerId}`);
      return response.data.data || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!accountId) return;
      setLoading(true);
      try {
        const [accountData, scheduleData] = await Promise.all([
          getPlAccount(accountId),
          fetchRepaymentSchedule(accountId).catch(() => [])
        ]);

        setAccount(accountData);
        setSchedule(scheduleData);

        if (accountData?.ci_customer_id) {
          const customerData = await getCustomerById(accountData.ci_customer_id);
          setCustomer(customerData);
        }
      } catch (error: any) {
        toast.showError(`Error loading schedule print data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accountId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <Loading message="Loading Repayment Schedule..." />;
  }

  if (!account) {
    return (
      <Box padding="large">
        <Text tone="critical" weight="strong">Loan account details not found.</Text>
      </Box>
    );
  }

  const instName = account.branch?.institute?.[normLang === "si" ? "name_ln2" : normLang === "ta" ? "name_ln3" : "name_ln1"] || account.branch?.institute?.name_ln1 || account[normLang === "si" ? "institute_name_ln2" : normLang === "ta" ? "institute_name_ln3" : "institute_name_ln1"] || account.institute_name || "";
  const branchName = account.branch?.[normLang === "si" ? "name_ln2" : normLang === "ta" ? "name_ln3" : "name_ln1"] || account.branch?.name_ln1 || "";
  const prodName = account.account_type?.[normLang === "si" ? "name_ln2" : normLang === "ta" ? "name_ln3" : "name_ln1"] || account.account_type?.name_ln1 || "";
  const custName = customer?.[normLang === "si" ? "full_name_ln2" : normLang === "ta" ? "full_name_ln3" : "full_name_ln1"] || customer?.full_name_ln1 || "";
  const currentDate = new Date().toLocaleDateString();
  const displayDecimals = account.account_type?.decimal_number === 0 ? 2 : (account.account_type?.decimal_number ?? 2);

  // Totals calculations
  const totalPrincipal = schedule.reduce((sum, item) => sum + Number(item.capital_ins || 0), 0);
  const totalInterest = schedule.reduce((sum, item) => sum + Number(item.interest_ins || 0), 0);
  const totalInstallmentSum = totalPrincipal + totalInterest;

  return (
    <div id="print-page" className="print-container">
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background-color: #fff !important;
            color: #000 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            size: A4 portrait;
            margin: 1cm;
          }
          #print-page {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
            box-sizing: border-box !important;
          }
          .print-table {
            width: 100% !important;
            font-size: 11px !important;
          }
        }
        .print-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          font-family: Inter, Roboto, Helvetica, Arial, sans-serif;
          color: #333;
        }
        .print-header {
          display: flex;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 2px solid #333;
          padding-bottom: 10px;
        }
        .logo-img {
          height: 50px;
          width: auto;
          margin-right: 15px;
        }
        .inst-title {
          font-size: 18px;
          font-weight: bold;
          text-transform: uppercase;
        }
        .branch-info {
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }
        .report-title {
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          margin: 20px 0;
          text-transform: uppercase;
          border-bottom: 1.5px solid #222;
          padding-bottom: 6px;
        }
        .meta-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 25px;
          font-size: 13px;
        }
        .meta-table td {
          padding: 6px 0;
        }
        .meta-label {
          font-weight: bold;
          width: 25%;
        }
        .meta-value {
          width: 25%;
        }
        .print-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
          font-size: 12px;
        }
        .print-table th {
          background-color: #f3f4f6;
          border: 1px solid #d1d5db;
          padding: 8px;
          font-weight: bold;
          text-align: left;
        }
        .print-table td {
          border: 1px solid #e5e7eb;
          padding: 8px;
        }
        .print-table tr:nth-child(even) {
          background-color: #fafafa;
        }
        .print-table th.align-right, .print-table td.align-right {
          text-align: right;
        }
        .print-table .total-row td {
          font-weight: bold;
          background-color: #f3f4f6;
          border-top: 2px solid #333;
          border-bottom: 2px solid #333;
        }
      `}</style>

      {/* Action panel */}
      <Box className="no-print" marginBottom="large" padding={"small"} display={"flex"} justifyContent={"spaceBetween"} alignItems={"center"} background="surface">
        <Text weight="strong">Loan Repayment Schedule Preview</Text>
        <Box display="flex" alignItems="center" style={{ gap: '15px' }}>
          <select
            value={printLang}
            onChange={(e) => setPrintLang(e.target.value)}
            style={{
              padding: '4px 8px',
              fontSize: '13px',
              borderRadius: '4px',
              border: '1px solid #c8ccd4',
              backgroundColor: '#fff',
              color: '#2b303a',
              outline: 'none',
              cursor: 'pointer',
              height: '32px',
              fontWeight: 500,
            }}
          >
            <option value="en">English (EN)</option>
            <option value="si">Sinhala (SI)</option>
            <option value="ta">Tamil (TA)</option>
          </select>
          <Actions>
            <Button size="small" onClick={handlePrint} tone="formAccent" icon={<IconPrint />}>Print</Button>
            <Button size="small" onClick={() => window.close()} variant="ghost">Close</Button>
          </Actions>
        </Box>
      </Box>

      {/* Report Header */}
      <div className="print-header">
        {logoUrl && <img src={logoUrl} alt="Logo" className="logo-img" />}
        <div>
          <div className="inst-title">{instName}</div>
          <div className="branch-info">
            {getLabel("branch")}: {branchName} | {getLabel("date")}: {currentDate}
          </div>
        </div>
      </div>

      <div className="report-title">
        {getLabel("title")}
      </div>

      {/* Meta Info */}
      <table className="meta-table">
        <tbody>
          <tr>
            <td className="meta-label">{getLabel("loanNo")}:</td>
            <td className="meta-value">{account.ref_account_number || account.account_number}</td>
            <td className="meta-label">{getLabel("customerName")}:</td>
            <td className="meta-value">{custName}</td>
          </tr>
          <tr>
            <td className="meta-label">{getLabel("productName")}:</td>
            <td className="meta-value">{prodName}</td>
            <td className="meta-label">{getLabel("nic")}:</td>
            <td className="meta-value">{customer?.nic || ""}</td>
          </tr>
          <tr>
            <td className="meta-label">{getLabel("capital")}:</td>
            <td className="meta-value">{account.capital != null ? formatCurrency(account.capital, displayDecimals) : ""}</td>
            <td className="meta-label">{getLabel("interest")}:</td>
            <td className="meta-value">{account.interest_rate ? `${account.interest_rate}%` : ""}</td>
          </tr>
          <tr>
            <td className="meta-label">{getLabel("period")}:</td>
            <td className="meta-value">
              {account.period ? (
                normLang === 'si' ? `මාස ${account.period}` : normLang === 'ta' ? `மாதங்கள் ${account.period}` : `${account.period} Months`
              ) : ""}
            </td>
            <td className="meta-label"></td>
            <td className="meta-value"></td>
          </tr>
        </tbody>
      </table>

      {/* Repayment Schedule Table */}
      <table className="print-table">
        <thead>
          <tr>
            <th>{getLabel("insNumber")}</th>
            <th>{getLabel("dueDate")}</th>
            <th className="align-right">{getLabel("principal")}</th>
            <th className="align-right">{getLabel("interestCol")}</th>
            <th className="align-right">{getLabel("totalInstallment")}</th>
            <th className="align-right">{getLabel("balance")}</th>
          </tr>
        </thead>
        <tbody>
          {schedule.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", padding: "16px" }}>No repayment schedule records found.</td>
            </tr>
          ) : (
            <>
              {schedule.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.installment_number}</td>
                  <td>{formatDateYMD(item.payment_date)}</td>
                  <td className="align-right">{formatCurrency(item.capital_ins || 0, displayDecimals)}</td>
                  <td className="align-right">{formatCurrency(item.interest_ins || 0, displayDecimals)}</td>
                  <td className="align-right">{formatCurrency(Number(item.capital_ins || 0) + Number(item.interest_ins || 0), displayDecimals)}</td>
                  <td className="align-right">{formatCurrency(item.balance || 0, displayDecimals)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={2}>{getLabel("total")}</td>
                <td className="align-right">{formatCurrency(totalPrincipal, displayDecimals)}</td>
                <td className="align-right">{formatCurrency(totalInterest, displayDecimals)}</td>
                <td className="align-right">{formatCurrency(totalInstallmentSum, displayDecimals)}</td>
                <td></td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LoanSchedulePrint;
